// Keyring (key-custody.md §1; I-1a Batch 3): the stored wraps of the data key,
// read BEFORE unlock (S32) and written at first run (S31) or when the person
// changes the app-lock secret / adds a recovery code. Only wraps live here — the
// data key itself is never stored in clear. "Delete everything" clears this
// store first (deletion.ts).

import { isKeyWrap, type KeyWrap, type WrapPurpose } from '../crypto'

import { type StoreDb, watchDone } from './schema'

/** A stored wrap, or null when there is none or it is damaged (never trusted unchecked). */
export async function readKeyWrap(db: StoreDb, purpose: WrapPurpose): Promise<KeyWrap | null> {
  const value = await db.get('keyring', purpose)
  return isKeyWrap(value) ? value : null
}

export async function hasKeyring(db: StoreDb): Promise<boolean> {
  return (await db.count('keyring')) > 0
}

/**
 * First run only: save the new wraps if, and only if, there is no keyring yet —
 * checked and written in ONE transaction with add-only writes. Returns false when
 * another copy finished first run already (its key must never be overwritten, or
 * every record it wrote would become unreadable).
 */
export async function createKeyring(db: StoreDb, wraps: readonly KeyWrap[]): Promise<boolean> {
  const tx = db.transaction('keyring', 'readwrite')
  const done = watchDone(tx)
  if ((await tx.store.count()) > 0) {
    await done
    return false
  }
  await Promise.all(wraps.map((wrap) => tx.store.add(wrap)))
  await done
  return true
}

/** Replace one wrap of the SAME data key (after re-wrap: new app-lock secret, new recovery code). */
export async function replaceKeyWrap(db: StoreDb, wrap: KeyWrap): Promise<void> {
  const tx = db.transaction('keyring', 'readwrite')
  const done = watchDone(tx)
  await tx.store.put(wrap)
  await done
}

export async function removeKeyWrap(db: StoreDb, purpose: WrapPurpose): Promise<void> {
  const tx = db.transaction('keyring', 'readwrite')
  const done = watchDone(tx)
  await tx.store.delete(purpose)
  await done
}
